import React from "react";
import DetailedView from "@/components/layouts/DetailedView";
import { useParams } from "react-router-dom";
import { usePartnerContacts } from "@/pages/partners/contacts/hooks";
import PartnerContactTopFields from "@/components/layouts/PartnerContactTopFields";
import PartnerContactDetailFields from "@/components/layouts/PartnerContactDetailFields/PartnerContactDetailFields";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ComputedFields } from "@/services/api/types";
import InvolvedIn from "@/components/layouts/InvolvedIn";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { usePartnerContactActions } from "@/services/hooks/entities/partnerContact/usePartnerContactActions";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { UserData } from "@/entities/users/types";
import { EntityData } from "@/services/api/types";

interface Props {
  userData: UserData;
}

const PartnerContactDetailedView: React.FC<Props> = ({ userData }) => {
  const { partnerContactId } = useParams<{ partnerContactId: string }>();

  const { isDropdownClosing } = useDropdownClosing();

  const {
    entities: [partnerContactEntity],
    isError: isPartnerContactError
  } = usePartnerContacts([partnerContactId || ""], {
    forceRefetch: true,
    computed_fields: ComputedFields.ALL
  });

  const {
    partnerContact,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getCreateOptions,
    getOptions,
    renderModals,
    onCreateLead,
    onCreateDeal,
    onCreateContract
  } = usePartnerContactActions(partnerContactEntity, userData, false, true);

  return (
    <>
      <DetailedView
        entityData={partnerContact as EntityData}
        entityType={EntityType.PARTNER_CONTACT}
        isError={isPartnerContactError}
        pageLink="/people"
        options={getOptions()}
        topRightComponent={
          <OptionsDropdownMenu
            options={getCreateOptions()}
            triggerIcon="add"
            tPrefix="detailedView.create"
            triggerDataTest="partner-contact-detailed-view-create-entity-button"
            iconSize="lg"
          />
        }
        onUpdateField={onUpdateField}
        isTasksDrawerOpen={isTasksDrawerOpen}
        toggleTasksDrawer={toggleTasksDrawer}
        topFields={
          <PartnerContactTopFields
            data-test="partner-contact-detailed-view-top-fields"
            partnerContact={partnerContact}
            onUpdateField={onUpdateField}
          />
        }
        overviewContent={
          <div
            data-test="partner-contact-detailed-view-overview"
            className="w-full flex flex-col gap-6"
          >
            <div className="w-full flex gap-6">
              <div
                data-test="partner-contact-detailed-view-detail-fields"
                className="flex-1"
              >
                <PartnerContactDetailFields
                  partnerContact={partnerContact}
                  onUpdateField={onUpdateField}
                  containerClassName="grid-cols-[minmax(0,30%),1fr]"
                />
              </div>
              <div
                data-test="partner-contact-detailed-view-involved-in"
                className="flex-1"
              >
                <InvolvedIn
                  entityId={partnerContact?.id || ""}
                  entityType={EntityType.PARTNER_CONTACT}
                  onCreateLead={onCreateLead}
                  onCreateDeal={onCreateDeal}
                  onCreateContract={onCreateContract}
                />
              </div>
            </div>
          </div>
        }
      />
      {renderModals(isDropdownClosing, false, undefined, userData)}
    </>
  );
};

export default PartnerContactDetailedView;
